import { Col, Form, Input, Modal, Radio, Row, Select, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { MdOutlineAddAPhoto, MdOutlineBedtime, MdOutlineRestaurant, MdOutlineWc } from "react-icons/md";
import { RiCloseCircleFill } from "react-icons/ri";
import ImageUploadButton from "../../../../../components/inputs/ImageUploadButton";
import DatePicker from "../../../../../components/inputs/MomentDatePicker";
import { cloudFrontUrl, serverUrl } from "../../../../../nestserver";

const { TimePicker } = DatePicker;
const { Option } = Select;

const TYPE_TABS = [
  { value: "meal", label: "Meal", icon: MdOutlineRestaurant },
  { value: "loo", label: "Loo", icon: MdOutlineWc },
  { value: "sleep", label: "Sleep", icon: MdOutlineBedtime },
];

/** Admin copy of the instructor daily-activity entry modal — identical form. */
const AddActivityModal = ({ open, onClose, onSaved, studentId, date }) => {
  const [form] = Form.useForm();
  const [type, setType] = useState("meal");
  const [saving, setSaving] = useState(false);
  const [photoKey, setPhotoKey] = useState(null);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setType("meal");
      setPhotoKey(null);
      form.setFieldsValue({ time: moment() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const day = moment(date).format("YYYY-MM-DD");
      const occurredAt = moment(day + " " + values.time.format("HH:mm"), "YYYY-MM-DD HH:mm").toISOString();

      const payload = {
        date: day,
        occurred_at: occurredAt,
        type,
        note: values.note || undefined,
      };
      if (type === "meal") {
        payload.meal_slot = values.meal_slot;
        payload.food = values.food;
        payload.amount = values.amount;
        payload.photo = photoKey || undefined;
      } else if (type === "loo") {
        payload.loo_kind = values.loo_kind;
      } else if (type === "sleep") {
        payload.sleep_start = occurredAt;
        if (values.sleep_end) {
          payload.sleep_end = moment(
            day + " " + values.sleep_end.format("HH:mm"),
            "YYYY-MM-DD HH:mm",
          ).toISOString();
        }
      }

      const res = await axios.post(
        `${serverUrl}/montessori/daily-activity/student/${studentId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        },
      );
      onSaved(res.data.result);
    } catch (err) {
      if (err?.errorFields) return;
      if (err?.response?.data?.message) {
        message.error(err.response.data.message);
      } else {
        console.error(err);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Log an activity"
      visible={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={saving}
      okText="Save entry"
      width={520}
      destroyOnClose
    >
      <Radio.Group
        value={type}
        onChange={(e) => setType(e.target.value)}
        className="mb-5 flex"
        optionType="button"
        buttonStyle="solid"
      >
        {TYPE_TABS.map((tab) => (
          <Radio.Button key={tab.value} value={tab.value} className="flex-1 text-center">
            <span className="inline-flex items-center gap-1.5">
              <tab.icon size={16} />
              {tab.label}
            </span>
          </Radio.Button>
        ))}
      </Radio.Group>

      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={type === "loo" ? 24 : 12}>
            <Form.Item
              label={type === "sleep" ? "Nap start" : "Time"}
              name="time"
              rules={[{ required: true, message: "Time is required" }]}
            >
              <TimePicker format="h:mm A" className="w-full" />
            </Form.Item>
          </Col>

          {type === "meal" && (
            <Col span={12}>
              <Form.Item label="Meal slot" name="meal_slot">
                <Select placeholder="Select slot" allowClear>
                  <Option value="breakfast">Breakfast</Option>
                  <Option value="am_snack">AM Snack</Option>
                  <Option value="lunch">Lunch</Option>
                  <Option value="pm_snack">PM Snack</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          {type === "meal" && (
            <>
              <Col span={16}>
                <Form.Item label="Food (optional)" name="food">
                  <Input placeholder="e.g. Dal rice + curd" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Amount eaten" name="amount">
                  <Select placeholder="Amount" allowClear>
                    <Option value="all">All</Option>
                    <Option value="most">Most</Option>
                    <Option value="some">Some</Option>
                    <Option value="none">None</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="Photo (optional)" className="mb-4">
                  <div className="relative inline-block">
                    <ImageUploadButton
                      folder={studentId}
                      allowedTypes={["image"]}
                      multiple={false}
                      maxSize={5}
                      image={photoKey ? `${cloudFrontUrl}/${studentId}/${photoKey}` : undefined}
                      onUpload={(id) => setPhotoKey(id)}
                    >
                      <div
                        className="flex flex-col items-center justify-center text-gray-400"
                        style={{
                          width: 80,
                          height: 80,
                          border: "1px dashed #d9d9d9",
                          borderRadius: 8,
                        }}
                      >
                        <MdOutlineAddAPhoto size={20} />
                        <div className="text-xs mt-1">Photo</div>
                      </div>
                    </ImageUploadButton>
                    {photoKey && (
                      <RiCloseCircleFill
                        size={18}
                        className="absolute cursor-pointer bg-white rounded-full"
                        style={{ top: -6, right: -6, color: "#dc2626" }}
                        onClick={() => setPhotoKey(null)}
                      />
                    )}
                  </div>
                </Form.Item>
              </Col>
            </>
          )}

          {type === "loo" && (
            <Col span={24}>
              <Form.Item label="Type" name="loo_kind">
                <Select placeholder="Select type" allowClear>
                  <Option value="diaper_wet">Diaper - wet</Option>
                  <Option value="diaper_soiled">Diaper - soiled</Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          {type === "sleep" && (
            <Col span={12}>
              <Form.Item label="Nap end (optional)" name="sleep_end">
                <TimePicker format="h:mm A" className="w-full" />
              </Form.Item>
            </Col>
          )}

          <Col span={24}>
            <Form.Item label="Note (optional)" name="note" className="mb-0">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddActivityModal;
